import React, { useEffect, useState } from 'react';
import { Box, Stack, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { UserType } from '../../../interfaces/userType.enum';
import { DashboardService } from '../../../services/dashboard.service';
import { AttendanceSummary, AttendanceKpis, AttendanceKpi, DailyCompositionPoint, ArrivalHistogram, WeekdayLatePoint, EmployeeLate, EmployeeOt, DepartmentAttendance, TodayLog, PunchLogRow } from '../../../interfaces/dashboard.interface';
import { WidgetDef, WidgetProps } from './types';
import Beeswarm from '../charts/Beeswarm';
import Columns from '../charts/Columns';
import StatTile from '../charts/StatTile';
import Sparkline from '../charts/Sparkline';
import StackedColumns from '../charts/StackedColumns';
import HBars from '../charts/HBars';
import HStack100 from '../charts/HStack100';
import TableView from '../charts/TableView';
import useKpiTargets from '../useKpiTargets';

const ADMIN_ROLES = [UserType.InstitutionAdmin, UserType.InstitutionManager];

const EmptyNote: React.FC<{ msg?: string }> = ({ msg }) => {
    const { t } = useTranslation('dashboard');
    return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>{msg ?? t('widgets.attendance.noShiftData')}</Typography>;
};

const WindowCaption: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{children}</Typography>
);

function useAttendance(institutionId: string) {
    const [data, setData] = useState<AttendanceSummary | null>(null);
    useEffect(() => {
        let live = true;
        DashboardService.getAttendanceSummary(institutionId).then(d => { if (live) setData(d); }).catch(() => {});
        return () => { live = false; };
    }, [institutionId]);
    return data;
}

const DayPulseWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { t } = useTranslation('dashboard');
    const d = useAttendance(institutionId);
    if (!d) return <CircularProgress size={20} />;
    return (
        <Box>
            <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                <StatTile label={t('widgets.attendance.present')} value={d.presentToday} sub={t('widgets.ofTotal', { total: d.totalUsers })} tone="good" />
                <StatTile label={t('widgets.attendance.onLeave')} value={d.onLeaveToday} />
                <StatTile label={t('widgets.attendance.absent')} value={d.absentToday} tone={d.absentToday > 0 ? 'warning' : 'default'} />
            </Stack>
            <Beeswarm minutes={d.arrivalMinutesToday} />
        </Box>
    );
};

const TrendWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const d = useAttendance(institutionId);
    if (!d) return <CircularProgress size={20} />;
    return <Columns data={d.trend.map(p => ({ label: p.date.slice(5), value: p.present }))} />;
};

// attendance.onTimeRate targets are framed as the complement of lateShare (100 − late%), matching
// the label on the KPI-targets settings screen rather than the raw late-share metric.
function statValueForTarget(key: keyof AttendanceKpis, k: AttendanceKpi): number {
    return key === 'lateShare' ? 100 - k.value : k.value;
}

const KPI_META: {
    key: keyof AttendanceKpis;
    labelKey: 'widgets.attendance.attendanceRate' | 'widgets.attendance.lateShare' | 'widgets.attendance.overtime' | 'widgets.attendance.avgDayWorked';
    targetKey?: string;
    // The late-share tile shows a raw late % but meters against the on-time complement, so its
    // caption must name the metric ("On-time target: 85%") or it reads as a wildly missed target.
    targetLabelKey?: 'widgets.attendance.targetPct' | 'widgets.attendance.onTimeTargetPct';
}[] = [
    { key: 'attendanceRate', labelKey: 'widgets.attendance.attendanceRate', targetKey: 'attendance.presentRate', targetLabelKey: 'widgets.attendance.targetPct' },
    { key: 'lateShare', labelKey: 'widgets.attendance.lateShare', targetKey: 'attendance.onTimeRate', targetLabelKey: 'widgets.attendance.onTimeTargetPct' },
    { key: 'overtimeHours', labelKey: 'widgets.attendance.overtime' },
    { key: 'avgDayWorked', labelKey: 'widgets.attendance.avgDayWorked' },
];

function fmtKpiValue(k: AttendanceKpi): string {
    return `${Number.isInteger(k.value) ? k.value : k.value.toFixed(1)}${k.unit}`;
}

// With no earlier window to compare against (a new institution), the server reports the previous
// value as 0, so the delta equals the value; "+92.0%" would read as a jump, so show a dash instead.
function hasComparison(k: AttendanceKpi): boolean {
    return Math.abs(k.value - k.delta) >= 0.05;
}

function fmtKpiDelta(k: AttendanceKpi): string {
    if (!hasComparison(k)) return '—';
    return `${k.delta >= 0 ? '+' : ''}${k.delta.toFixed(1)}${k.unit}`;
}

// tone follows improvement direction, not raw value: a metric where lower is better
// (late share, overtime) is "good" when its delta is negative.
function kpiTone(k: AttendanceKpi): 'default' | 'good' | 'warning' {
    if (k.delta === 0 || !hasComparison(k)) return 'default';
    const improving = k.higherIsBetter ? k.delta > 0 : k.delta < 0;
    return improving ? 'good' : 'warning';
}

const KpisWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { t } = useTranslation('dashboard');
    const [d, setD] = useState<AttendanceKpis | null>(null);
    const targets = useKpiTargets(institutionId);
    useEffect(() => {
        let live = true;
        DashboardService.getAttendanceKpis(institutionId).then(x => { if (live) setD(x); }).catch(() => {});
        return () => { live = false; };
    }, [institutionId]);
    if (!d) return <CircularProgress size={20} />;
    if (d.attendanceRate.sparkline.length === 0) return <EmptyNote />;
    return (
        <Box>
        <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
            {KPI_META.map(({ key, labelKey, targetKey, targetLabelKey }) => {
                const k = d[key];
                const target = targetKey ? targets[targetKey] : undefined;
                const value = statValueForTarget(key, k);
                const meter = target != null && target > 0 ? Math.max(0, Math.min(100, (value / target) * 100)) : undefined;
                return (
                    <Box key={key} sx={{ minWidth: 150 }}>
                        <StatTile label={t(labelKey)} value={fmtKpiValue(k)} sub={fmtKpiDelta(k)} tone={kpiTone(k)} target={target} meter={meter} targetLabel={target != null && targetLabelKey ? t(targetLabelKey, { target }) : undefined} />
                        <Box sx={{ px: 1.5 }}><Sparkline values={k.sparkline} /></Box>
                    </Box>
                );
            })}
        </Stack>
        {/* Rolling-window figures, not today's: without the caption a morning reading looks like a missed target. */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {t('widgets.windows.kpis')}
        </Typography>
        </Box>
    );
};

const CompositionWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { t } = useTranslation('dashboard');
    const [d, setD] = useState<DailyCompositionPoint[] | null>(null);
    useEffect(() => {
        let live = true;
        DashboardService.getAttendanceComposition(institutionId).then(x => { if (live) setD(x); }).catch(() => {});
        return () => { live = false; };
    }, [institutionId]);
    if (!d) return <CircularProgress size={20} />;
    if (d.length === 0) return <EmptyNote />;
    return (
        <Box>
            <StackedColumns
                data={d.map(p => ({ label: p.date.slice(5), segments: [p.onTime, p.late, p.onLeave, p.absent] }))}
                seriesLabels={[t('widgets.attendance.onTime'), t('widgets.attendance.late'), t('widgets.attendance.leave'), t('widgets.attendance.absent')]}
                colorIndices={[0, 1, 3, 2]}
                tickEvery={7}
            />
            <WindowCaption>{t('widgets.windows.last60ShiftBased')}</WindowCaption>
        </Box>
    );
};

function minuteToSlot(min: number): string {
    const h = Math.floor(min / 60);
    const m = min % 60 < 30 ? '00' : '30';
    return `${String(h).padStart(2, '0')}:${m}`;
}

const ArrivalsWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { t } = useTranslation('dashboard');
    const [d, setD] = useState<ArrivalHistogram | null>(null);
    useEffect(() => {
        let live = true;
        DashboardService.getAttendanceArrivals(institutionId).then(x => { if (live) setD(x); }).catch(() => {});
        return () => { live = false; };
    }, [institutionId]);
    if (!d) return <CircularProgress size={20} />;
    if (d.buckets.length === 0) return <EmptyNote msg={t('widgets.attendance.noArrivals')} />;
    const markerSlot = d.shiftStartMinute != null ? minuteToSlot(d.shiftStartMinute) : null;
    const markerIndex = markerSlot != null ? d.buckets.findIndex(b => b.slot === markerSlot) : -1;
    const marker = markerIndex >= 0 ? { index: markerIndex, label: markerSlot as string } : undefined;
    return (
        <Box>
            <Columns data={d.buckets.map(b => ({ label: b.slot, value: b.count }))} marker={marker} />
            <WindowCaption>{t('widgets.windows.arrivals')}</WindowCaption>
        </Box>
    );
};

const WeekdayWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { t } = useTranslation('dashboard');
    const [d, setD] = useState<WeekdayLatePoint[] | null>(null);
    useEffect(() => {
        let live = true;
        DashboardService.getAttendanceWeekday(institutionId).then(x => { if (live) setD(x); }).catch(() => {});
        return () => { live = false; };
    }, [institutionId]);
    if (!d) return <CircularProgress size={20} />;
    if (d.length === 0 || d.every(p => p.worked === 0)) return <EmptyNote />;
    return (
        <Box>
            <Columns
                data={d.map(p => ({ label: p.weekday, value: p.worked > 0 ? Math.round((p.late / p.worked) * 100) : 0 }))}
                colorIndex={1}
                yFmt={(n) => `${n}%`}
            />
            <WindowCaption>{t('widgets.windows.last60')}</WindowCaption>
        </Box>
    );
};

const TopLateWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { t } = useTranslation('dashboard');
    const [d, setD] = useState<EmployeeLate[] | null>(null);
    useEffect(() => {
        let live = true;
        DashboardService.getAttendanceTopLate(institutionId).then(x => { if (live) setD(x); }).catch(() => {});
        return () => { live = false; };
    }, [institutionId]);
    if (!d) return <CircularProgress size={20} />;
    if (d.length === 0) return <EmptyNote />;
    return (
        <Box>
        <HBars
            data={d.map(e => ({
                label: e.name,
                value: e.lateDays,
                tooltip: [
                    { label: t('widgets.attendance.lateDays'), value: String(e.lateDays) },
                    { label: t('widgets.attendance.lateMinutes'), value: String(e.lateMinutes) },
                ],
            }))}
            colorIndex={1}
        />
        <WindowCaption>{t('widgets.windows.topLate')}</WindowCaption>
        </Box>
    );
};

const TopOtWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { t } = useTranslation('dashboard');
    const [d, setD] = useState<EmployeeOt[] | null>(null);
    useEffect(() => {
        let live = true;
        DashboardService.getAttendanceTopOt(institutionId).then(x => { if (live) setD(x); }).catch(() => {});
        return () => { live = false; };
    }, [institutionId]);
    if (!d) return <CircularProgress size={20} />;
    if (d.length === 0) return <EmptyNote />;
    return (
        <Box>
        <HBars
            data={d.map(e => {
                const hours = Math.round((e.overtimeMinutes / 60) * 10) / 10;
                return {
                    label: e.name,
                    value: hours,
                    tooltip: [
                        { label: t('widgets.attendance.overtime'), value: t('widgets.attendance.hours', { hours }) },
                        { label: t('widgets.attendance.daysWorked'), value: String(e.daysWorked) },
                    ],
                };
            })}
            colorIndex={4}
        />
        <WindowCaption>{t('widgets.windows.topOvertime')}</WindowCaption>
        </Box>
    );
};

const DepartmentWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { t } = useTranslation('dashboard');
    const [d, setD] = useState<DepartmentAttendance[] | null>(null);
    useEffect(() => {
        let live = true;
        DashboardService.getAttendanceDepartment(institutionId).then(x => { if (live) setD(x); }).catch(() => {});
        return () => { live = false; };
    }, [institutionId]);
    if (!d) return <CircularProgress size={20} />;
    if (d.length === 0) return <EmptyNote />;
    return (
        <Box>
            <HStack100
                rows={d.map(x => ({ label: x.department, segments: [x.onTime, x.late, x.absent] }))}
                seriesLabels={[t('widgets.attendance.onTime'), t('widgets.attendance.late'), t('widgets.attendance.absent')]}
                colorIndices={[0, 1, 2]}
            />
            <WindowCaption>{t('widgets.windows.last60ShiftBased')}</WindowCaption>
        </Box>
    );
};

function minToHHmm(min: number | null): string {
    if (min == null) return '—';
    const h = Math.floor(min / 60);
    const m = min % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

const TodayLogWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { t } = useTranslation('dashboard');
    const [d, setD] = useState<TodayLog | null>(null);
    useEffect(() => {
        let live = true;
        DashboardService.getAttendanceTodayLog(institutionId).then(x => { if (live) setD(x); }).catch(() => {});
        return () => { live = false; };
    }, [institutionId]);
    if (!d) return <CircularProgress size={20} />;
    if (d.rows.length === 0) return <EmptyNote msg={t('widgets.attendance.noPunchesToday')} />;
    return (
        <Box>
            <TableView
                columns={[t('widgets.attendance.colName'), t('widgets.attendance.colIn'), t('widgets.attendance.colOut'), t('widgets.attendance.colLate')]}
                rows={d.rows.map((r: PunchLogRow) => [
                    r.name,
                    minToHHmm(r.inMinute),
                    // A lone check-in stores out == in; show "—" (still in) rather than a fake 0-minute day.
                    r.outMinute != null && r.outMinute === r.inMinute ? '—' : minToHHmm(r.outMinute),
                    r.late ? t('widgets.attendance.minutesLate', { minutes: r.lateMinutes }) : t('widgets.attendance.onTime'),
                ])}
            />
            {d.absentNotShown > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {t('widgets.attendance.absentNotShown', { n: d.absentNotShown })}
                </Typography>
            )}
        </Box>
    );
};

export const attendanceWidgets: WidgetDef[] = [
    { id: 'attendance.dayPulse', module: 'attendance', titleKey: 'widgets.titles.attendanceDayPulse', subtitleKey: 'widgets.subtitles.attendanceDayPulse', roles: ADMIN_ROLES, size: 'lg', component: DayPulseWidget },
    { id: 'attendance.trend', module: 'attendance', titleKey: 'widgets.titles.attendanceTrend', subtitleKey: 'widgets.subtitles.attendanceTrend', roles: ADMIN_ROLES, size: 'md', component: TrendWidget },
    { id: 'attendance.kpis', module: 'attendance', titleKey: 'widgets.titles.attendanceKpis', subtitleKey: 'widgets.subtitles.attendanceKpis', roles: ADMIN_ROLES, size: 'lg', component: KpisWidget },
    { id: 'attendance.composition', module: 'attendance', titleKey: 'widgets.titles.attendanceComposition', subtitleKey: 'widgets.subtitles.attendanceComposition', roles: ADMIN_ROLES, size: 'lg', component: CompositionWidget },
    { id: 'attendance.arrivals', module: 'attendance', titleKey: 'widgets.titles.attendanceArrivals', subtitleKey: 'widgets.subtitles.attendanceArrivals', roles: ADMIN_ROLES, size: 'md', component: ArrivalsWidget },
    { id: 'attendance.weekday', module: 'attendance', titleKey: 'widgets.titles.attendanceWeekday', subtitleKey: 'widgets.subtitles.attendanceWeekday', roles: ADMIN_ROLES, size: 'md', component: WeekdayWidget },
    { id: 'attendance.topLate', module: 'attendance', titleKey: 'widgets.titles.attendanceTopLate', subtitleKey: 'widgets.subtitles.attendanceTopLate', roles: ADMIN_ROLES, size: 'md', component: TopLateWidget },
    { id: 'attendance.topOt', module: 'attendance', titleKey: 'widgets.titles.attendanceTopOt', subtitleKey: 'widgets.subtitles.attendanceTopOt', roles: ADMIN_ROLES, size: 'md', component: TopOtWidget },
    { id: 'attendance.department', module: 'attendance', titleKey: 'widgets.titles.attendanceDepartment', subtitleKey: 'widgets.subtitles.attendanceDepartment', roles: ADMIN_ROLES, size: 'lg', component: DepartmentWidget },
    { id: 'attendance.todayLog', module: 'attendance', titleKey: 'widgets.titles.attendanceTodayLog', subtitleKey: 'widgets.subtitles.attendanceTodayLog', roles: ADMIN_ROLES, size: 'lg', component: TodayLogWidget },
];
